import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { Size } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import type { Orientation } from "./ScrollBar.js";

export interface SeparatorOptions extends ControlOptions {
  orientation?: Orientation;
  /** null uses the theme's grid line color. */
  color?: Color | null;
  /** Line thickness in px. */
  thickness?: number;
  /** Gap left at each end, so the rule stops short of the edges. */
  inset?: number;
}

/**
 * A rule between groups of controls.
 *
 * "Vertical" describes the line, not the direction it divides in: a vertical
 * separator is an upright line, the kind that goes between two toolbar
 * sections. Menus have their own separators (`menuSeparator`) because a menu
 * item is a spec rather than a control; this is the one for a form.
 */
export class Separator extends Control {
  #orientation: Orientation;

  color: Color | null;
  thickness: number;
  inset: number;

  constructor(options: SeparatorOptions = {}) {
    const vertical = (options.orientation ?? "horizontal") === "vertical";
    super({
      width: vertical ? 1 : 100,
      height: vertical ? 100 : 1,
      tabStop: false,
      ...options,
    });
    this.#orientation = options.orientation ?? "horizontal";
    this.color = options.color ?? null;
    this.thickness = options.thickness ?? 1;
    this.inset = options.inset ?? 0;
  }

  get orientation(): Orientation {
    return this.#orientation;
  }
  set orientation(v: Orientation) {
    if (this.#orientation === v) return;
    this.#orientation = v;
    this.invalidate();
  }

  get vertical(): boolean {
    return this.#orientation === "vertical";
  }

  /** As thick as the line plus its inset, and as long as the room allows. */
  override preferredSize(available: Size): Size {
    const thick = Math.max(1, this.thickness);
    return this.vertical
      ? { width: thick, height: available.height }
      : { width: available.width, height: thick };
  }

  /** A rule is decoration; clicks belong to whatever is behind it. */
  protected override hitTestSelf(): boolean {
    return false;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const ink = this.color ?? theme.palette.gridLine;
    const t = Math.max(1, this.thickness);
    if (this.vertical) {
      const length = Math.max(0, this.height - this.inset * 2);
      rc.fillRect(
        { x: Math.round((this.width - t) / 2), y: this.inset, width: t, height: length },
        ink,
      );
      return;
    }
    const length = Math.max(0, this.width - this.inset * 2);
    rc.fillRect(
      { x: this.inset, y: Math.round((this.height - t) / 2), width: length, height: t },
      ink,
    );
  }
}
